/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { queryOptions } from '@tanstack/react-query';
import { sessionStorageProvider } from '@opengeoweb/shared';
import { getPluginCatalog, TEST_PLUGIN_ID } from './api';
import type { PluginRegistry } from './api';

const LIB_KEY = 'plugins-lib';
const API_KEY = 'plugins-api';
const TEST_PLUGIN_REFETCH_INTERVAL_MS = 2000;

const generatePluginsQueryKeys = (...queryKeys: string[]): string[] => [
  LIB_KEY,
  API_KEY,
  ...queryKeys,
];

const getPluginCatalogConfig = (): {
  backendUrl: string;
  testUrl: string;
  hasTestPluginUrl: boolean;
} => {
  const config = sessionStorageProvider.getConfig();
  const backendUrl = config?.GW_PLUGIN_BACKEND_URL?.trim() || '';
  const testUrl = config?.GW_PLUGIN_TEST_URL?.trim() || '';

  return {
    backendUrl,
    testUrl,
    hasTestPluginUrl: testUrl.length > 0,
  };
};

const getLiveReloadQueryOptions = (
  hasTestPluginUrl: boolean,
): {
  refetchInterval?:
    | number
    | false
    | ((query: { state: { data?: PluginRegistry } }) => number | false);
} =>
  hasTestPluginUrl
    ? {
        refetchInterval: (query): number | false => {
          const { data } = query.state;

          const hasWorkingTestPlugin = !!data?.[TEST_PLUGIN_ID];

          return hasWorkingTestPlugin ? TEST_PLUGIN_REFETCH_INTERVAL_MS : false;
        },
      }
    : {};

export const pluginsQueryOptions = {
  getPluginCatalog: () => {
    const { backendUrl, testUrl, hasTestPluginUrl } = getPluginCatalogConfig();

    return queryOptions({
      queryKey: generatePluginsQueryKeys(
        'list',
        `backend:${backendUrl}`,
        `test:${testUrl}`,
      ),
      queryFn: getPluginCatalog,
      staleTime: 0,
      refetchOnMount: true,
      ...getLiveReloadQueryOptions(hasTestPluginUrl),
    });
  },
} as const;
